import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { VALIDATION_FIELD_MAPPING } from '../../../../../utils/appConstant';
import ElipsisCell from '../../../../../components/TableBox/Cells/EllipsisCell';
import ElipsisText from '../../../../../components/ElipsisText';
import Badge from '../../../../../components/Badge';
import Button from '../../../../../components/Button';
import {
  RowClassName,
  Modules,
  AuthRoutes,
  RouteParamsTypes,
  PermissionModulesEnum,
  PermissionActionsEnum,
} from '../../../../../enums';
import { TableContext } from '../../../../../contexts/TableContext';
import { AuthContext } from '../../../../../contexts/AuthContext';
import { hasResourseAccess } from '../../../../../utils/methods';
import { ProductResponse } from '../../../../../interfaces/product.interface';

interface RowProps {
  item: ProductResponse;
  className?: RowClassName;
}

const Row: React.FC<RowProps> = ({ item, className }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { headCells } = useContext(TableContext);
  const { isSystemAdmin, permissions } = useContext(AuthContext);

  const hasEditAccess = hasResourseAccess(isSystemAdmin, permissions, {
    accessibleTo: [{ [PermissionModulesEnum.Products]: [PermissionActionsEnum.Update] }],
  });

  const handleEditClick = (id: string) => {
    navigate(`${Modules.products}${AuthRoutes.products}${RouteParamsTypes.edit}/${id}`);
  };

  // Modifier name on top, date below — same pattern as users module
  const updatedNode = (
    <div>
      <div className="d-block text-truncate" style={{ maxWidth: '150px' }}>
        {item.modify_by?.first_name} {item.modify_by?.last_name}
      </div>
      <ElipsisText
        width="100%"
        text={item.modify_date ? new Date(item.modify_date).toLocaleDateString() : '-'}
      />
    </div>
  );

  return (
    <tr className={className}>
      {headCells.map((headCell) => {
        if (headCell.id === VALIDATION_FIELD_MAPPING.name) {
          return <ElipsisCell key={headCell.id} width={headCell.width} text={item.name || '-'} />;
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.description) {
          return <ElipsisCell key={headCell.id} width={headCell.width} text={item.description || '-'} />;
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.product_division) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              text={item.product_division?.name || '-'}
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.status) {
          return (
            <td key={headCell.id} width={headCell.width}>
              <Badge
                text={
                  item.is_active
                    ? t('products.list.pageTexts.table.status.active')
                    : t('products.list.pageTexts.table.status.inActive')
                }
                variant={item.is_active ? 'success' : 'danger'}
              />
            </td>
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.modify_date) {
          return <ElipsisCell key={headCell.id} width={headCell.width} node={updatedNode} />;
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.actions) {
          return (
            <td key={headCell.id} width={headCell.width}>
              <div className="d-flex align-items-sm-center justify-content-sm-start">
                {hasEditAccess && (
                  <Button
                    className="btn btn-sm btn-icon btn-text-secondary waves-effect waves-light rounded-pill"
                    icon={<i className="ri-edit-box-line ri-20px"></i>}
                    onClick={() => handleEditClick(item.id)}
                  />
                )}
              </div>
            </td>
          );
        }

        const value = item[headCell.id as keyof ProductResponse];
        return <ElipsisCell key={headCell.id} width={headCell.width} text={value ? String(value) : '-'} />;
      })}
    </tr>
  );
};

export default Row;
